import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardMedia,
  Divider,
  makeStyles
} from "@material-ui/core";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import * as FileApi from "../api";

const useStyles = makeStyles(_ => ({
  root: {
    textAlign: "center"
  },
  divider: {
    margin: "0px"
  }
}));

interface Props {
  fileId?: string | null;
  label?: string;
  onChange: (fileId?: string) => void;
  height?: number | string;
  disabled?: boolean;
}

interface StateProps {
  isLoading: boolean;
  file?: IFileUpload;
  imageBlob?: string;
}

const ImageUpload = (props: Props) => {
  const classes = useStyles();
  const { onChange, disabled } = props;
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<StateProps>({ isLoading: false });
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchFile = useCallback(
    async (fileId: string) => {
      setState(s => ({ ...s, isLoading: true }));
      try {
        const infoResponse = await FileApi.info(fileId);
        const response = await FileApi.download(fileId);
        const blob = await response.blob();
        const imageBlob = URL.createObjectURL(blob);
        setState({
          isLoading: false,
          file: infoResponse.value,
          imageBlob: imageBlob
        });
      } catch (e) {
        handleError(e);
        setState({ isLoading: false });
      }
    },
    [handleError]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      setState(s => ({ ...s, isLoading: true }));
      try {
        const response = await FileApi.upload([file]);
        const fileUpload = response.value.files[0];
        const imageBlob = URL.createObjectURL(file);
        setState({
          isLoading: false,
          file: fileUpload,
          imageBlob: imageBlob
        });
        onChange(fileUpload.id);
      } catch (e) {
        handleError(e);
        setState({ isLoading: false });
      }
    },
    [handleError, onChange]
  );

  const deleteFile = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onChange(undefined);
  }, [onChange]);

  const handleCapture = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.files) {
      uploadFile(target.files[0]);
    }
  };

  useEffect(() => {
    if (props.fileId) {
      fetchFile(props.fileId);
    } else {
      setState({ isLoading: false });
    }
  }, [props.fileId, fetchFile]);

  const height = props.height ?? "160px";

  return (
    <Card>
      {state.imageBlob ? (
        <CardMedia
          component="img"
          alt="Image"
          src={state.imageBlob}
          height={height}
          style={{ objectFit: "contain" }}
        />
      ) : (
        <CardMedia>
          <div
            style={{
              height: height,
              lineHeight: height,
              textAlign: "center"
            }}
          >
            No Image
          </div>
        </CardMedia>
      )}
      <Divider light className={classes.divider} />
      <CardActions>
        <Button
          component="label"
          color="primary"
          startIcon={<AddPhotoAlternateIcon />}
          disabled={disabled}
        >
          {props.label ? `เลือก${props.label}` : "Upload"}
          <input
            ref={inputRef}
            accept=".jpg,.jpeg,.png"
            hidden
            id="upload-photo"
            type="file"
            onChange={handleCapture}
          />
        </Button>
        {state.imageBlob && (
          <Button
            color="primary"
            startIcon={<DeleteOutlinedIcon />}
            onClick={deleteFile}
            disabled={disabled}
          >
            ลบรูป
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ImageUpload;
